<template>
	<div class="EditReportChecklists">
		<!-- Checklists header -->
		<div class="Page-section Page-section--primaryLight">
			<div class="Page-section-inner Page-section-inner--md">
				<CloHeading level="2">{{ $t('tripReport.manageChecklists') }}</CloHeading>
				<p class="u-margin-none">
					{{ $t('tripReport.manageChecklistsDesc') }}
				</p>
				<!-- Personal dates module -->
				<section aria-labelledby="personal-dates" class="EditReportChecklists-dates">
					<CloAccordion>
						<CloAccordionItem :startExpanded="currentUser.role === 'participant'">
							<template v-slot:heading>
								<CloHeading id="personal-dates" level="3" size="4">{{ $t('tripReport.personalDates') }}</CloHeading>
							</template>
							<template v-slot:content>
								<p class="u-stack-sm">
									{{ $t('tripReport.personalDatesDesc') }}
								</p>
								<!-- Personal dates picker -->
								<div class="EditReportChecklists-dates-picker">
									<!-- Dates -->
									<CloGridAuto gap="md">
										<CloDatePicker
											:key="componentKey"
											enableTimeRange
											:locale="locale"
											:showMonths="2"
											dateFormat="Y-m-d"
											altFormat="j F Y"
											mode="range"
											ref="dateRangeTimePicker"
											:label="$t('tripReport.startDateLabel')"
											:endLabel="$t('tripReport.endDateLabel')"
											:startTimeLabel="$t('tripReport.startTimeLabel')"
											:endTimeLabel="$t('tripReport.endTimeLabel')"
											:placeholder="$t('tripReport.startDatePlaceholder')"
											:endPlaceholder="$t('tripReport.endDatePlaceholder')"
											:defaultDateRange="[startDateTime, endDateTime]"
											v-on:on-change="setSelectedDateRange"
											:confirmText="$t('generic.Close')"
											:defaultStartHour="startHours.toString()"
											:defaultStartMinute="startMinutes.toString()"
											:defaultEndHour="endHours.toString()"
											:defaultEndMinute="endMinutes.toString()"
											:minTimeStart="minTimeStart"
											:maxTimeEnd="maxTimeEnd"
											:minDate="tripReportModelData.beginDt"
											:maxDate="tripReportModelData.endDt"
											@on-change-end-time="setSelectedEndTime"
											@on-change-start-time="setSelectedStartTime"
										>
										</CloDatePicker>
									</CloGridAuto>
								</div>
								<div class="EditReportChecklists-dates-actions">
									<CloButton type="button" @click.native="saveExcludedChecklists(true)">{{
										$t('tripReport.savePersonalDates')
									}}</CloButton>
									<CloButton @click.native="resetPersonalDatesForm" hollow>{{ $t('generic.Reset') }}</CloButton>
								</div>
							</template>
						</CloAccordionItem>
					</CloAccordion>
				</section>
			</div>
		</div>
		<!-- Checklists content -->
		<div class="Page-section">
			<div class="Page-section-inner Page-section-inner--md">
				<section class="u-stack-lg">
					<!-- Header (stat and all checklists checkbox) -->
					<div class="u-stack-md">
						<div class="GridFlex GridFlex--flex GridFlex--withGutter GridFlex--alignMiddle">
							<div class="GridFlex-cell u-sizeFill">
								<CloStat
									id="stat-checklists"
									class="StatsIcon--inline"
									icon="checklist"
									:stat="numChecklists"
									:label="$t('generic.Checklists')"
									checked
								></CloStat>
							</div>
							<div class="GridFlex-cell u-sizeFit">
								<CloInputCheckbox
									:label="$t('generic.AllChecklists')"
									id="select-all-checklists"
									name="allChecklists"
									v-model="allSubIdsSelected"
									@change="setDataHasChanged"
								/>
							</div>
						</div>
					</div>
					<form>
						<ul class="EditReportChecklists-list">
							<CloLoadingAnimation
								v-if="loadingChecklists"
								class="LoadingAnimation LoadingAnimation--large LoadingAnimation--basic"
							/>
							<!-- TODO: Add .u-excluded class to li based on !checked -->
							<li v-for="checklist in checklists" :key="checklist.subId">
								<div class="GridFlex GridFlex--flex GridFlex--withGutter">
									<div class="GridFlex-cell u-sizeFill">
										<ChecklistItem :checklist="checklist" :edit="true" />
									</div>
									<div class="GridFlex-cell u-sizeFit">
										<CloInputCheckbox
											:label="$t('tripReport.includeChecklist', [checklist.subId])"
											:id="`select-checklist-${checklist.subId}`"
											:name="checklist.subId"
											:value="checklist.subId"
											v-model="checkedSubIds"
											@change="setDataHasChanged"
										/>
									</div>
								</div>
							</li>
						</ul>
					</form>
				</section>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloButton from '@/base/CloButton/CloButton.vue';
import CloAccordion from '@/navigation/CloAccordion/CloAccordion.vue';
import CloAccordionItem from '@/navigation/CloAccordion/CloAccordionItem.vue';
import CloDatePicker from '@/forms/CloDatePicker/CloDatePicker.vue';
import CloGridAuto from '@/grid/CloGridAuto/CloGridAuto.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation';
import CloStat from './CloStat.vue';
import CloInputCheckbox from '@/forms/CloInputCheckbox/CloInputCheckbox.vue';
import ChecklistItem from './ChecklistItem.vue';
export default {
	name: 'EditReportChecklists',
	components: {
		CloDatePicker,
		CloHeading,
		CloButton,
		CloAccordion,
		CloAccordionItem,
		CloGridAuto,
		CloLoadingAnimation,
		CloStat,
		CloInputCheckbox,
		ChecklistItem,
	},
	data() {
		return {
			allSubIds: [],
			baseUrl: window.baseUrl,
			tripManageApiBaseUrl: window.tripManageApiBaseUrl,
			tripReportModelData: window.tripReport ? window.tripReport : '',
			checklists: [],
			checkedSubIds: [],
			csrf: window['_csrf'],
			csrf_header: window['_csrf_header'],
			loadingChecklists: true,
			numChecklists: '',
			dataHasChanged: false,
			isFirstVisit: false,
			// for storing month/day/year portion
			startDateTime: '',
			// for storing month/day/year portion
			endDateTime: '',
			// for storing time portion
			startHours: '0',
			startMinutes: '0',
			// for storing time portion
			endHours: '23',
			endMinutes: '59',
			tripStartDate: new Date(window.tripReport.beginDt),
			tripEndDate: new Date(window.tripReport.endDt),
			minTimeStart: '',
			maxTimeEnd: '',
			componentKey: 0,
		};
	},
	props: {
		view: {
			type: String,
			default: 'definition',
		},
		mode: {
			type: String,
			default: 'create',
		},
		reportId: {
			type: Number,
			default: 0,
		},
		currentUser: {
			type: Object,
			default: null,
		},
		locale: String,
	},
	mounted() {
		// KAGU-1742 - so am/pm change works, eventually get this into lichen
		this.$refs.dateRangeTimePicker.instanceStartTime.config.dateFormat = 'G:i K';
		this.$refs.dateRangeTimePicker.instanceEndTime.config.dateFormat = 'G:i K';

		this.getChecklists();
		this.setIsFirstVisit();
		this.setMinMaxRange();
		this.enforceStartTimeLimit();
		this.enforceEndTimeLimit();
	},
	created() {
		this.initTimes();
	},
	methods: {
		setSelectedDateRange() {
			this.startDateTime = this.$refs.dateRangeTimePicker.instance.selectedDates[0];
			this.endDateTime = this.$refs.dateRangeTimePicker.instance.selectedDates[1];

			this.enforceStartTimeLimit();
			this.enforceEndTimeLimit();
			this.setDataHasChanged();
		},
		// min max for personal dates
		setMinMaxRange() {
			this.$refs.dateRangeTimePicker.instance.config.minDate = this.tripReportModelData.beginDt;
			this.$refs.dateRangeTimePicker.instance.config.maxDate = this.tripReportModelData.endDt;
		},
		setIsFirstVisit() {
			const queryString = window.location.search;
			const urlParams = new URLSearchParams(queryString);
			const welcome = urlParams.get('welcome');
			if (welcome) {
				this.isFirstVisit = true;
			}
		},
		setDataHasChanged() {
			this.dataHasChanged = true;
		},
		setSelectedStartTime() {
			let dateStr = this.$refs.dateRangeTimePicker.instanceStartTime.latestSelectedDateObj;
			let start = new Date(dateStr);
			this.startHours = start.getHours();
			this.startMinutes = start.getMinutes();
			this.enforceStartTimeLimit();
			this.setDataHasChanged();
		},
		setSelectedEndTime() {
			let dateStr = this.$refs.dateRangeTimePicker.instanceEndTime.latestSelectedDateObj;
			let end = new Date(dateStr);
			this.endHours = end.getHours();
			this.endMinutes = end.getMinutes();
			this.enforceEndTimeLimit();
			this.setDataHasChanged();
		},
		getChecklists() {
			let url = `${this.tripManageApiBaseUrl}checklists/${this.reportId}`;
			axios
				.get(url)
				.then((res) => {
					// api returns null if no comments
					if (res.data) {
						this.setAllSubIds(res.data);
						this.loadingChecklists = false;
					}
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		// initialize included/excluded subs
		setAllSubIds(data) {
			this.checklists = data;
			this.allSubIds = this.checklists.map(function (el) {
				return el.subId;
			});

			let excludedChecklists = this.checklists.filter(function (el) {
				return el.excluded == false;
			});
			this.checkedSubIds = excludedChecklists.map(function (el) {
				return el.subId;
			});

			this.numChecklists = this.checklists.length.toString();
		},
		selectAllSubIds() {
			this.checkedSubIds = this.allSubIds;
		},
		deselectAllSubIds() {
			this.checkedSubIds = [];
		},
		saveExcludedChecklists(keepManaging) {
			let excluded = this.getExcludedChecklists();
			let welcomeParamStr = this.isFirstVisit ? '?welcome=true' : '';
			if (this.dataHasChanged) {
				let url = `${this.tripManageApiBaseUrl}exclude-lists/${this.reportId}`;
				let formData = new FormData();
				formData.append('subIds', excluded);
				let headers = {};
				headers[this.csrf_header] = this.csrf;
				axios
					.post(url, formData, {
						headers: headers,
						withCredentials: true,
					})
					.then((res) => {
						if (!keepManaging) {
							window.location = baseUrl + 'tripreport/' + this.reportId + welcomeParamStr;
						} else {
							this.savePersonalDates();
						}
					})
					.catch((err) => {
						console.log(err);
					});
			} else if (!keepManaging) {
				window.location = baseUrl + 'tripreport/' + this.reportId + welcomeParamStr;
			}
		},
		getExcludedChecklists() {
			return this.allSubIds.filter((x) => !this.checkedSubIds.includes(x));
		},
		savePersonalDates() {
			this.setDataHasChanged();
			//this.prepareDatesForSubmit();
			let clearPersonalDates = this.personalDatesMatchTripDates();
			let url = '';
			if (!clearPersonalDates) {
				url = `${this.tripManageApiBaseUrl}set-personal-dates/${this.reportId}/${this.formattedStartDateTime}/${this.formattedEndDateTime}`;
			} else {
				url = `${this.tripManageApiBaseUrl}clear-personal-dates/${this.reportId}`;
			}

			let headers = {};
			headers[this.csrf_header] = this.csrf;
			axios
				.post(
					url,
					{},
					{
						headers: headers,
						withCredentials: true,
					}
				)
				.then((res) => {
					// refetch lists
					this.getChecklists();
				})
				.catch((err) => {
					console.log(err);
				});
		},
		resetPersonalDatesForm() {
			this.initTimes(true);
			this.componentKey += 1;
			this.enforceStartTimeLimit();
			this.enforceEndTimeLimit();
			this.setDataHasChanged();
		},
		getTimeFormattedDate(datetime) {
			let date = new Date(datetime);
			return date.getHours() + ':' + date.getMinutes();
		},
		initTimes(reset) {
			if (this.currentUser.personBeginDt && !reset) {
				this.startDateTime = this.currentUser.personBeginDt;
				this.endDateTime = this.currentUser.personEndDt;
			} else {
				this.startDateTime = this.tripReportModelData.beginDt;
				this.endDateTime = this.tripReportModelData.endDt;
			}

			let date = new Date(this.startDateTime);
			this.startHours = date.getHours();
			this.startMinutes = date.getMinutes();

			date = new Date(this.endDateTime);
			this.endHours = date.getHours();
			this.endMinutes = date.getMinutes();
		},
		personalDatesMatchTripDates() {
			let tripStart = new Date(this.tripReportModelData.beginDt);
			let personalStart = new Date(this.startDateTime);
			personalStart.setHours(this.startHours);
			personalStart.setMinutes(this.startMinutes);

			let tripEnd = new Date(this.tripReportModelData.endDt);
			let personalEnd = new Date(this.endDateTime);
			personalEnd.setHours(this.endHours);
			personalEnd.setMinutes(this.endMinutes);

			let sameStart = tripStart.getTime() === personalStart.getTime();
			let sameEnd = tripEnd.getTime() === personalEnd.getTime();

			return sameStart && sameEnd;
		},
		enforceStartTimeLimit() {
			let startDate = this.$refs.dateRangeTimePicker.instance.selectedDates[0];
			let personalStart = new Date(startDate);
			let personalStartYear = personalStart.getFullYear();
			let personalStartMonth = personalStart.getMonth();
			let personalStartDay = personalStart.getDay();

			if (
				this.tripStartYear === personalStartYear &&
				this.tripStartMonth === personalStartMonth &&
				this.tripStartDay === personalStartDay
			) {
				let personalStartTime = new Date(this.$refs.dateRangeTimePicker.instanceStartTime.selectedDates[0]);

				/**
				 * If start time is below the min (user switched date back to trip start date after decreasing
				 * the start time on another date) reset to trip start time
				 */
				if (
					personalStartTime.getHours() < this.tripStartHours ||
					(personalStartTime.getHours() == this.tripStartHours && personalStartTime.getMinutes() < this.tripStartMinutes)
				) {
					this.$refs.dateRangeTimePicker.instanceStartTime.setDate(this.tripStartTime);
					this.startHours = this.tripStartHours;
					this.startMinutes = this.tripStartMinutes;
				}
				// enforce min start time
				this.$refs.dateRangeTimePicker.instanceStartTime.config.minDate = this.tripStartTime;
			} else {
				this.$refs.dateRangeTimePicker.instanceStartTime.config.minDate = '';
			}
		},
		enforceEndTimeLimit() {
			let endDate = this.$refs.dateRangeTimePicker.instance.selectedDates[1];
			let personalEnd = new Date(endDate);
			let personalEndYear = personalEnd.getFullYear();
			let personalEndMonth = personalEnd.getMonth();
			let personalEndDay = personalEnd.getDay();

			if (this.tripEndYear === personalEndYear && this.tripEndMonth === personalEndMonth && this.tripEndDay === personalEndDay) {
				let personalEndTime = new Date(this.$refs.dateRangeTimePicker.instanceEndTime.selectedDates[0]);
				/**
				 * If end time is above the max (user switched date back to trip end date after increasing the end time on another date),
				 * reset to trip end time
				 */
				if (
					personalEndTime.getHours() > this.tripEndHours ||
					(personalEndTime.getHours() == this.tripEndHours && personalEndTime.getMinutes() > this.tripEndMinutes)
				) {
					this.$refs.dateRangeTimePicker.instanceEndTime.setDate(this.tripEndTime);
					this.endHours = this.tripEndHours;
					this.endMinutes = this.tripEndMinutes;
				}
				// enforce max end time
				this.$refs.dateRangeTimePicker.instanceEndTime.config.maxDate = this.tripEndTime;
			} else {
				this.$refs.dateRangeTimePicker.instanceEndTime.config.maxDate = '';
			}
		},
		resetDates() {
			this.$emit('setAreFieldsFilled', false);
		},
	},
	computed: {
		allSubIdsSelected: {
			get: function () {
				return this.getExcludedChecklists().length === 0;
			},
			set: function (isChecked) {
				if (isChecked) {
					this.selectAllSubIds();
				} else {
					this.deselectAllSubIds();
				}
			},
		},
		formattedStartDateTime() {
			let start = new Date(this.startDateTime);
			start.setHours(this.startHours);
			start.setMinutes(this.startMinutes);
			let tzOffsetStart = start.getTimezoneOffset();
			let formattedDateStart = new Date(start.getTime() - tzOffsetStart * 60 * 1000);
			return formattedDateStart.toISOString().replace(':00.000Z', '');
		},
		formattedEndDateTime() {
			let end = new Date(this.endDateTime);
			end.setHours(this.endHours);
			end.setMinutes(this.endMinutes);
			let tzOffsetEnd = end.getTimezoneOffset();
			let formattedDateEnd = new Date(end.getTime() - tzOffsetEnd * 60 * 1000);
			return formattedDateEnd.toISOString().replace(':00.000Z', '');
		},
		tripStartYear() {
			return this.tripStartDate.getFullYear();
		},
		tripStartMonth() {
			return this.tripStartDate.getMonth();
		},
		tripStartDay() {
			return this.tripStartDate.getDay();
		},
		tripStartHours() {
			return this.tripStartDate.getHours();
		},
		tripStartMinutes() {
			return this.tripStartDate.getMinutes();
		},
		tripEndYear() {
			return this.tripEndDate.getFullYear();
		},
		tripEndMonth() {
			return this.tripEndDate.getMonth();
		},
		tripEndDay() {
			return this.tripEndDate.getDay();
		},
		tripEndHours() {
			return this.tripEndDate.getHours();
		},
		tripEndMinutes() {
			return this.tripEndDate.getMinutes();
		},
		tripStartTime() {
			return this.tripStartDate.getHours() + ':' + this.tripStartDate.getMinutes();
		},
		tripEndTime() {
			return this.tripEndDate.getHours() + ':' + this.tripEndDate.getMinutes();
		},
	},
};
</script>

<style lang="scss">
.EditReportChecklists-header {
	// background-color: $color-primary-0;
	// @include inset-md;
	// @include stack-md;
}
.EditReportChecklists-list {
	list-style: none;
	margin-left: 0;
	margin-right: 0;
	[dir='rtl'] & {
		margin-left: 0;
		margin-right: 0;
	}
	margin-bottom: $space-lg;
	background-color: $color-neutral-1;
	display: flex;
	flex-direction: column;
	padding-top: 1px;
	gap: 1px;
	margin-left: var(--paddingToFill, -($space-md));
	margin-right: var(--paddingToFill, -($space-md));
	width: calc(100% + calc(var(--paddingtoFill, ($space-md)) * 2));
	@include breakpoint(medium) {
		margin-left: 0;
		margin-right: 0;
		width: 100%;
	}
	li {
		background-color: $color-white;
		@include inset-squish-md;
		@include breakpoint(medium) {
			padding-left: 0;
			padding-right: 0;
		}
	}
	li.u-excluded {
		background-color: transparent;
	}
}
.EditReportChecklists .InputCheckbox {
	display: flex;
	align-items: center;
	flex-direction: row-reverse;
	gap: $space-sm;
	input {
		accent-color: $color-secondary-3;
		width: $space-md + $space-sm;
		height: $space-md + $space-sm;
	}
}
.EditReportChecklists-list .InputCheckbox label {
	@include visuallyHidden;
}

.EditReportChecklists-dates {
	.EditReportChecklists-dates-actions {
		display: flex;
		align-items: center;
		gap: $space-md;
		.Button {
			margin: 0;
		}
	}
}
</style>
